import { getLogger } from '@logtape/logtape';
import { Tabs } from '@mantine/core';
import { FC } from 'react';

import CardReader from '@/components/CardReader.tsx';
import ConditionalTooltip from '@/components/ConditionalTooltip.tsx';

import useConfig from '@/hooks/useConfig.ts';

import { GenericPatient } from '@/models/GenericPatient.ts';
import { NotificationType } from '@/models/Notification.ts';
import { PatientFormData } from '@/models/Patient.ts';
import { Insurance } from '@/models/appointment/Appointment.ts';
import { PVSProvider } from '@/models/customers/PVSProvider.ts';

import {
    showLoadingNotification,
    showWarningNotification,
    updateNotification,
} from '@/services/notificationService.ts';
import { createUser } from '@/services/patientService.ts';

import { useBookingStore } from '@/stores/bookingStore.ts';
import { WalkInModalMode, WalkInTab, useWalkInStore } from '@/stores/walkInStore.ts';

import { validatePatientForm } from '@/utils/formUtils.ts';
import { updateCurrentPatient } from '@/utils/patientUtils.ts';

import { PatientDataForm } from './form/patient-data-form.tsx';
import { SearchPatient } from './searchPatient/search-patient.tsx';

const logger = getLogger(['component', 'walk-in-modal']);

interface Props {
    onConfirm: () => void;
    mode: WalkInModalMode;
}

const WalkInModal: FC<Props> = ({ onConfirm, mode }) => {
    const { selected_customer, getInstanceConfig, instance_ids } = useConfig();
    const { active_tab, setActiveTab, setIsUserSelected, setLoading } = useWalkInStore();

    const isCardReadEnabled = instance_ids.some((id) => getInstanceConfig(id)?.pvs_provider === PVSProvider.DOC_CIRRUS);

    // Show search tab only for CREATE_PATIENT mode
    const show_search_tab = mode !== WalkInModalMode.CREATE_PATIENT;

    // TODO: add proper logic for switching to appointment booking modal
    const createPatient = async (values: PatientFormData): Promise<void> => {
        const config = getInstanceConfig(values.instance_id);

        if (!config) {
            throw new Error('Instance config not found');
        }

        const { show_notification, message } = validatePatientForm(values, config.pvs_provider, false);
        if (show_notification) {
            showWarningNotification(message ?? '', 'Fehlende Informationen');
            return;
        }

        setLoading(true);
        const notification_id: string = showLoadingNotification('Patient anlegen...');

        try {
            // Use the patient service to create the patient
            const created_user: GenericPatient | undefined = await createUser(
                values,
                config.pvs_provider,
                selected_customer.is_data_admission_to_pvs_approved
            );

            if (!created_user) {
                logger.error('Error while creating patient');
                updateNotification(NotificationType.ERROR, {
                    id: notification_id,
                    message: 'Patient anlegen fehlgeschlagen!',
                });
                setLoading(false);
                return;
            }

            useBookingStore.setState((state) => ({
                initial_form_values: {
                    ...state.initial_form_values,
                    practice_doc_cirrus_id: values.location_id,
                    insurance: Insurance[values.insurance_type],
                },
            }));

            // Then update the store
            updateCurrentPatient(created_user);

            startBooking();

            updateNotification(NotificationType.SUCCESS, {
                id: notification_id,
                message: 'Patient erfolgreich angelegt!',
            });
        } catch (error) {
            logger.error('Failed to create the patient', { error: error });
            updateNotification(NotificationType.ERROR, {
                id: notification_id,
                message: 'Patient anlegen fehlgeschlagen!',
            });
        }

        setLoading(false);
    };

    const startBooking = (): void => {
        if (onConfirm) onConfirm();
    };

    return (
        <div className="flex min-h-[62vh] flex-col">
            <Tabs
                value={active_tab}
                onChange={(value) => setActiveTab(value as WalkInTab | undefined)}
                className="h-[90%]"
            >
                <Tabs.List className="mb-2">
                    {show_search_tab && <Tabs.Tab value="search">Patient:in suchen</Tabs.Tab>}
                    <ConditionalTooltip label={isCardReadEnabled ? '' : 'Nicht verfügbar für diese Instanz'}>
                        <Tabs.Tab disabled={!isCardReadEnabled} value="read-card">
                            Karte lesen
                        </Tabs.Tab>
                    </ConditionalTooltip>
                    <Tabs.Tab value="new-patient">Neue Akte erstellen</Tabs.Tab>
                </Tabs.List>
                {show_search_tab && (
                    <Tabs.Panel value="search">
                        <SearchPatient setIsPatientSelected={setIsUserSelected} />
                    </Tabs.Panel>
                )}
                <Tabs.Panel value="read-card" className="h-full">
                    <CardReader />
                </Tabs.Panel>
                <Tabs.Panel value="new-patient">
                    <PatientDataForm
                        isEdit={false}
                        handleSubmit={(values) => createPatient(values as PatientFormData)}
                    />
                </Tabs.Panel>
            </Tabs>
        </div>
    );
};

export default WalkInModal;
